import { GroupRowType } from "helpers/models";
import { Content } from "pdfmake/interfaces";
import formatNumber from "report/common/format-number";
import layout from "report/common/layout";

const get = (
  t: any,
  data: GroupRowType[],
  aggregationUnits: string[],
  text: string = t("fathom.Return Period"),
  unit: string = "numberOfLocations",
  currency: string = ""
): Content => {
  const currencyColumnKey = t(`locations.add.currency`);
  const currencyColumnValue = currency;

  const aggHeader = aggregationUnits.map((agg) => ({
    text: t(`locations.${agg}`),
    alignment: "right"
  }));

  const header = [
    { text: text, bold: true, colSpan: 2 },
    {},
    { text: currencyColumnKey, alignment: "right" },
    ...aggHeader,
    {
      text: t("report.Share", { key: t(`locations.${unit}`) }),
      alignment: "right"
    },
    {}
  ];

  const widths = header.map((_, i) => {
    if (i === 0) return 15; // Color box
    if (i === 1) return 100; // Label
    if (i === header.length - 1) return 150; // Percentage bar
    return "*";
  });

  const totalCount = data.reduce((sum, item) => sum + item.count, 0);
  const totalAgg = data.reduce(
    (sum, item) => sum + Number(item[unit as keyof GroupRowType] || 0),
    0
  );
  const total = unit === "numberOfLocations" ? totalCount : totalAgg;

  const row = data.map((e) => {
    const percentCount = total > 0 ? (e.count / total) * 100 : 0;
    const percentAgg =
      totalAgg > 0
        ? (Number(e[unit as keyof GroupRowType] || 0) / totalAgg) * 100
        : 0;
    const percent = unit === "numberOfLocations" ? percentCount : percentAgg;

    const aggColumn = aggregationUnits.map((agg) => {
      if (agg === "numberOfLocations")
        return { text: e.count.toString(), alignment: "right" };
      const v = e[agg as keyof GroupRowType];
      if (v === null || v === undefined) {
        return { text: " - ", alignment: "right" };
      }
      const fn = formatNumber(v as number);
      return { text: fn, alignment: "right" };
    });

    return [
      {
        canvas: [
          {
            type: "rect",
            x: 0,
            y: 0,
            w: 12,
            h: 12,
            lineColor: "#E4E4E4",
            lineWidth: 1,
            color: e.color || "#CCCCCC"
          }
        ]
      },

      t("legend." + e.label),

      { text: currencyColumnValue, alignment: "right" },

      ...aggColumn,

      { text: percent.toFixed(2) + "%", alignment: "right" },

      {
        canvas: [
          {
            type: "rect",
            x: 0,
            y: 3,
            w: percent * 1.5,
            h: 4,
            color: e.color || "#CCCCCC"
          }
        ],
        alignment: "right"
      }
    ];
  });

  const summary = data.reduce((acc, curr) => {
    aggregationUnits.forEach((key) => {
      if (key === "numberOfLocations") {
        acc[key] = (acc[key] || 0) + curr.count;
        return;
      }
      if (typeof curr[key as keyof GroupRowType] === "number") {
        const k = key as keyof typeof acc;
        acc[k] = (acc[k] || 0) + (curr[key as keyof GroupRowType] as number);
      }
    });
    return acc;
  }, {} as { [key: string]: number });

  const footerAggValues = aggregationUnits.map((agg) => ({
    text:
      summary[agg] !== undefined && summary[agg] !== null
        ? formatNumber(summary[agg]).toString()
        : " - ",
    alignment: "right"
  }));

  const footer = [
    { text: t("report.Total"), colSpan: 2 },
    {},
    { text: currencyColumnValue, alignment: "right" }, // Placeholder for currency column
    ...footerAggValues,
    { text: "100%", alignment: "right" },
    {
      canvas: [
        {
          type: "rect",
          x: 0,
          y: 3,
          w: 150,
          h: 4,
          color: "black"
        }
      ],
      alignment: "right"
    }
  ];

  return {
    table: {
      headerRows: 1,
      widths,
      body: [header, ...row, footer]
    },
    layout: layout(0, true, 6),

    unbreakable: true
  };
};

export default get;
